import { Component, Input } from '@angular/core';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

/** One button in the actions cell, as a page describes it for one row. The label comes from the ⋮ menu item of the same action. */
export interface ListActionButtonSpec {
    action: string;
    /** A PrimeIcons class (`pi-sparkles`) in the Actions column, or a Font Awesome icon on the hover icons. */
    icon?: string | IconDefinition;
    /** Show the icon alone, with the label as the tooltip. Use for a dense action where the glyph reads on its own. */
    iconOnly?: boolean;
    /** Semantic emphasis, matching the detail-drawer buttons: `danger` for a destructive action, `primary` for the main one, else neutral. */
    tone?: 'primary' | 'danger';
    /** What a screen reader hears. Defaults to the label; give the row's name where several rows show the same button. */
    ariaLabel?: string;
    /** The id of the button, for specs and e2e selectors. Defaults to the cell id and the action. */
    id?: string;
    busy?: () => boolean;
    /** The text the button shows while busy, such as `Reading…`. */
    busyTitle?: string;
    /** Where the action cannot run yet, such as a document that does not exist. The title then says why. */
    disabled?: () => boolean;
    /** Keeps the button, disabled, on a row whose menu hides the action, so it can say why. */
    disabledTitle?: string;
    /** A destructive action, such as Delete. The hover-actions cell turns it red on hover. */
    danger?: boolean;
    /** A page the hover-actions cell opens in a new tab. The button is then a link, and `run` is not called. */
    href?: string;
}

/** One button as the cell draws it. */
export interface ListActionButton extends ListActionButtonSpec {
    label: string;
}

/**
 * A list-table cell with the row's quick actions as text or icon buttons. A button stops its click, so it does not also open the
 * row, and says what it is doing while busy. Each action is also in the ⋮ menu, which keeps it when a narrow screen hides this column.
 */
@Component({
    selector: 'app-list-actions-cell',
    standalone: true,
    template: `
        <span class="lt-actions">
            @for (button of buttons; track button.action) {
            <button
                type="button"
                class="lt-action"
                [class.lt-action--icon]="button.iconOnly"
                [class.lt-action--primary]="button.tone === 'primary'"
                [class.lt-action--danger]="button.tone === 'danger'"
                [id]="button.id || cellId + '_' + button.action"
                [disabled]="isBusy(button) || isDisabled(button)"
                [title]="titleOf(button)"
                [attr.aria-label]="button.ariaLabel || button.label"
                (click)="run(button.action); $event.stopPropagation()"
            >
                @if (isBusy(button)) {
                <i class="pi pi-spinner pi-spin" aria-hidden="true"></i>
                } @else if (button.icon) {
                <i class="pi {{ button.icon }}" aria-hidden="true"></i>
                }
                @if (!button.iconOnly) {
                <span class="lt-action__label">{{ isBusy(button) ? button.busyTitle || 'Working…' : button.label }}</span>
                }
            </button>
            }
        </span>
    `,
    styles: [
        `
            .lt-actions {
                align-items: center;
                display: inline-flex;
                gap: 0.5rem;
            }

            /* Tonal quick-action button, matching the detail-drawer action buttons (.fd-detail__action): a soft
               text-tinted fill and a strong edge so it reads as a button rather than the old flat link. */
            .lt-action {
                align-items: center;
                background-color: color-mix(in srgb, var(--tableText) 8%, transparent);
                border: 0.0625rem solid var(--nf-border-strong);
                border-radius: var(--inputBorderRadius);
                color: var(--tableText);
                cursor: pointer;
                display: inline-flex;
                font-size: 0.75rem;
                font-weight: 600;
                gap: 0.375rem;
                line-height: 1;
                padding: 0.3125rem 0.625rem;
                transition: var(--transition);
                white-space: nowrap;
            }

            .lt-action .pi {
                font-size: 0.875rem;
                line-height: 1;
            }

            /* The icon-only button (e.g. Explain) squares up so the glyph sits centred. */
            .lt-action--icon {
                padding: 0.3125rem 0.4375rem;
            }

            .lt-action:hover:not(:disabled) {
                background-color: color-mix(in srgb, var(--tableText) 26%, transparent);
                border-color: color-mix(in srgb, var(--tableText) 58%, transparent);
            }

            /* The main action (brand), like Edit in the drawer. */
            .lt-action--primary {
                background-color: color-mix(in srgb, var(--primary) 13%, transparent);
                border-color: color-mix(in srgb, var(--primary) 34%, transparent);
                color: var(--primary);
            }

            .lt-action--primary:hover:not(:disabled) {
                background-color: color-mix(in srgb, var(--primary) 30%, transparent);
                border-color: color-mix(in srgb, var(--primary) 62%, transparent);
            }

            /* A destructive action, like Cancel or Delete. */
            .lt-action--danger {
                background-color: color-mix(in srgb, var(--red) 13%, transparent);
                border-color: color-mix(in srgb, var(--red) 34%, transparent);
                color: var(--red);
            }

            .lt-action--danger:hover:not(:disabled) {
                background-color: color-mix(in srgb, var(--red) 30%, transparent);
                border-color: color-mix(in srgb, var(--red) 62%, transparent);
            }

            .lt-action:disabled {
                cursor: not-allowed;
                opacity: 0.5;
            }
        `,
    ],
})
export class ListActionsCellComponent {
    @Input() buttons: ListActionButton[] = [];
    /** The prefix of each button id, for specs and e2e selectors. */
    @Input() cellId = '';
    @Input() run: (action: string) => void = () => undefined;

    isBusy(button: ListActionButton): boolean {
        return button.busy?.() ?? false;
    }

    isDisabled(button: ListActionButton): boolean {
        return button.disabled?.() ?? false;
    }

    titleOf(button: ListActionButton): string {
        if (this.isBusy(button)) {
            return button.busyTitle || 'Working…';
        }
        if (this.isDisabled(button)) {
            return button.disabledTitle || button.label;
        }
        return button.label;
    }
}
